import { useSyncExternalStore } from "react";
import type { FileRef } from "@studio-features/meta-crew/library-adapter";
import type { RefineBatchInput, RefineUploadJob } from "@studio-features/meta-crew/library-refine-upload";
import type { UploadTrayBatch, UploadTrayRow, UploadTrayView } from "@studio-features/meta-crew/refine-upload-tray";
import { uploadActive } from "@studio-features/meta-crew/refine-upload-tray";
import { api, errorMessage } from "./api";
import { HQ_SCOPE, uploadAdminFile } from "./library-adapter";

/**
 * 정형화 업로드 전역 큐 (요청서 34절, 시안 7d124a1 RefineUploadTray 의 호스트 쪽).
 * 올리기 창은 선택만 넘기고 닫힌다(enqueue). 여기서 탭이 열려 있는 동안 묶음을 순서대로 처리한다: 행마다 텍스트(.md/.txt/.json/.csv) 읽기 →
 * 나머지 파일 관리자 파일(`/admin/files`)로 올리기 → `POST /admin/manuscripts/refine/batch`(행 1건). 화면 이동과 무관하고(모듈 단위 저장소),
 * 실패 행만 재시도(올린 파일 참조·읽은 텍스트 재사용), 취소는 남은 것만 멈추고 만든 작업은 둔다. 진행 중이면 탭 닫기 경고(beforeunload).
 * 탭을 닫으면 멈춘다 — 파일은 브라우저에만 있으므로 복구하지 않는다.
 */
const TEXT = /\.(md|txt|json|csv)$/i;
const MANUSCRIPT = /\.(md|txt)$/i;

type BatchResponse = { items: { path: string; ok: boolean; id?: string; error?: string; code?: string }[] };

type Work = {
  input: RefineUploadJob;
  refs: Map<File, FileRef>;
  texts: Map<File, string>;
  cancel: boolean;
  view: UploadTrayBatch;
};

class RefineUploadQueue {
  private works: Work[] = [];
  private listeners = new Set<() => void>();
  private created = new Set<(ids: string[]) => void>();
  private activeId: string | null = null;
  private snapshot: UploadTrayView = { batches: [] };
  private notice?: UploadTrayView["notice"];
  private seq = 0;
  private unloadGuard = false;

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };
  getSnapshot = () => this.snapshot;
  /** 작업이 만들어질 때마다 (목록 다시 읽기용) */
  onCreated = (l: (ids: string[]) => void) => {
    this.created.add(l);
    return () => {
      this.created.delete(l);
    };
  };

  private emit() {
    this.snapshot = { batches: this.works.map((w) => ({ ...w.view, rows: w.view.rows.map((r) => ({ ...r })) })), notice: this.notice };
    this.listeners.forEach((l) => l());
    this.guard();
  }

  /** 진행 중 묶음이 있을 때만 탭 닫기 경고 */
  private guard() {
    const active = this.works.some((w) => uploadActive(w.view));
    if (active && !this.unloadGuard) {
      window.addEventListener("beforeunload", onBeforeUnload);
      this.unloadGuard = true;
    } else if (!active && this.unloadGuard) {
      window.removeEventListener("beforeunload", onBeforeUnload);
      this.unloadGuard = false;
    }
  }

  /** 올리기 창에서 넘어온 선택을 접수한다 (동기, 실패하면 throw → 창에 표시) */
  enqueue = (input: RefineUploadJob, customerName?: string) => {
    if (!input.rows.length) throw new Error("올릴 폴더가 없습니다.");
    if (input.rows.length > 50) throw new Error("한 번에 최대 50개 폴더를 올릴 수 있습니다.");
    const id = `up-${Date.now()}-${++this.seq}`;
    const frozen: RefineUploadJob = { ...input, rows: input.rows.map((r) => ({ ...r, files: [...r.files] })) };
    const rows: UploadTrayRow[] = frozen.rows.map((r, i) => {
      const has = r.files.some((f) => MANUSCRIPT.test(f.name));
      return { id: String(i), path: r.path, state: has ? "waiting" : "skipped", filesDone: 0, filesTotal: r.files.length, ...(has ? {} : { error: "텍스트 파일 없음(.md/.txt) · 건너뜀" }) };
    });
    this.works.push({ input: frozen, refs: new Map(), texts: new Map(), cancel: false, view: { id, customer: customerName ?? input.customerId, label: `정형화 업로드 ${this.seq}`, state: "waiting", rows } });
    this.emit();
    void this.pump();
  };

  private finish(w: Work) {
    const ids = w.view.rows.flatMap((r) => (r.jobId ? [r.jobId] : []));
    const failed = w.view.rows.filter((r) => r.state === "failed").length;
    const skipped = w.view.rows.filter((r) => r.state === "skipped").length;
    w.view.state = w.cancel ? "cancelled" : failed ? "partial" : "complete";
    this.notice = {
      id: `${w.view.id}:${Date.now()}`,
      message: w.cancel ? `업로드를 취소했습니다 · 만든 작업 ${ids.length}개 유지` : `정형화 작업 ${ids.length}개를 만들었습니다${failed ? ` · ${failed}건 실패` : ""}${skipped ? ` · ${skipped}건 건너뜀` : ""}`,
      ids,
    };
    this.emit();
  }

  private async pump() {
    if (this.activeId) return;
    const w = this.works.find((x) => x.view.state === "waiting");
    if (!w) return;
    this.activeId = w.view.id;
    w.view.state = "running";
    this.emit();
    for (let i = 0; i < w.view.rows.length; i++) {
      const row = w.view.rows[i]!;
      const input = w.input.rows[i]!;
      if (row.state !== "waiting") continue;
      if (w.cancel) {
        row.state = "cancelled";
        continue;
      }
      try {
        row.state = "uploading";
        this.emit();
        const texts: RefineBatchInput["items"][number]["texts"] = [];
        const files: { fileId: string }[] = [];
        for (const file of input.files) {
          if (w.cancel) break;
          if (TEXT.test(file.name)) {
            let body = w.texts.get(file);
            if (body === undefined) {
              body = await file.text();
              w.texts.set(file, body);
            }
            texts.push({ name: file.name, body });
          } else {
            let ref = w.refs.get(file);
            if (!ref) {
              ref = await uploadAdminFile(file);
              w.refs.set(file, ref);
            }
            files.push({ fileId: ref.fileId });
          }
          row.filesDone = input.files.filter((f) => w.refs.has(f) || w.texts.has(f)).length;
          this.emit();
        }
        if (w.cancel) {
          row.state = "cancelled";
          this.emit();
          continue;
        }
        row.state = "creating";
        this.emit();
        const { customerId, folderId, guidelineId, model, instructions } = w.input;
        const r = await api.post<BatchResponse>("/admin/manuscripts/refine/batch", {
          customerId: customerId && customerId !== HQ_SCOPE ? customerId : undefined,
          folderId: folderId || undefined,
          guidelineId: guidelineId || undefined,
          model: model || undefined,
          instructions: instructions?.trim() || undefined,
          items: [{ path: input.path, title: input.title?.trim() || undefined, texts, files }],
        });
        const saved = r.items.find((x) => x.path === input.path) ?? r.items[0];
        if (!saved?.ok || !saved.id) throw new Error(saved?.error ?? saved?.code ?? "작업 ID를 확인하지 못했습니다. 처리 결과를 확인해 주세요.");
        row.state = "complete";
        row.jobId = saved.id;
        row.error = undefined;
        this.created.forEach((l) => l([saved.id!]));
      } catch (e) {
        row.state = "failed";
        row.error = errorMessage(e);
      }
      this.emit();
    }
    if (w.cancel) w.view.rows.forEach((r) => r.state === "waiting" && (r.state = "cancelled"));
    this.finish(w);
    this.activeId = null;
    void this.pump();
  }

  retry = (id: string) => {
    const w = this.works.find((x) => x.view.id === id);
    if (!w || uploadActive(w.view) || !w.view.rows.some((r) => r.state === "failed")) return;
    w.view.rows.forEach((r) => {
      if (r.state === "failed") {
        r.state = "waiting";
        r.error = undefined;
      }
    });
    w.cancel = false;
    w.view.state = "waiting";
    this.emit();
    void this.pump();
  };
  cancel = (id: string) => {
    const w = this.works.find((x) => x.view.id === id);
    if (!w || !uploadActive(w.view)) return;
    w.cancel = true;
    if (this.activeId === id) {
      w.view.state = "cancelling";
      this.emit();
    } else {
      w.view.rows.forEach((r) => ["waiting", "uploading", "creating"].includes(r.state) && (r.state = "cancelled"));
      this.finish(w);
      void this.pump();
    }
  };
  dismiss = (id: string) => {
    this.works = this.works.filter((w) => w.view.id !== id || uploadActive(w.view));
    this.emit();
  };
  dismissNotice = () => {
    this.notice = undefined;
    this.emit();
  };
}

function onBeforeUnload(e: BeforeUnloadEvent) {
  e.preventDefault();
}


/** 콘솔 전역 하나 (화면이 바뀌어도 유지) */
export const refineUploadQueue = new RefineUploadQueue();

export function useRefineUploadQueue(): UploadTrayView {
  return useSyncExternalStore(refineUploadQueue.subscribe, refineUploadQueue.getSnapshot, refineUploadQueue.getSnapshot);
}
